import React, { useRef } from 'react';
import { Card, Field, TextField, SaveButton } from '../AdminUI';
import { useSite } from '../../../context/SiteContext';
import { toast } from 'sonner';
import { Plus, Trash2, Upload } from 'lucide-react';
import { Switch } from '../../ui/switch';

function fileToDataUrl(file, maxSize = 400) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = () => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const s = Math.min(maxSize / width, maxSize / height);
          width = Math.round(width * s);
          height = Math.round(height * s);
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/png', 0.9));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function MarqueeSection() {
  const { data, update } = useSite();
  const [marquee, setMarquee] = React.useState(data.logoMarquee || { title: '', items: [] });
  const [visibility, setVisibility] = React.useState(data.socialVisibility || { whatsapp: true, telegram: true, instagram: true, youtube: true });
  // Sync from server data only on mount; otherwise live polling would
  // overwrite the admin's in-progress edits.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    setMarquee(data.logoMarquee || { title: '', items: [] });
    setVisibility(data.socialVisibility || { whatsapp: true, telegram: true, instagram: true, youtube: true });
  }, []);

  const save = () => { update((p) => ({ ...p, logoMarquee: marquee, socialVisibility: visibility })); toast.success('Saved successfully'); };

  const upd = (i, k, v) => setMarquee({ ...marquee, items: marquee.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it) });
  const add = () => setMarquee({ ...marquee, items: [...marquee.items, { label: 'NEW BRAND', image: '' }] });
  const rem = (i) => setMarquee({ ...marquee, items: marquee.items.filter((_, idx) => idx !== i) });
  const onUpload = async (i, file) => {
    if (!file) return;
    const dataUrl = await fileToDataUrl(file, 400);
    upd(i, 'image', dataUrl);
  };

  return (
    <>
      <Card title="Logo Marquee" desc="Trusted-brand logos that scroll infinitely below the hero banner." footer={<SaveButton onClick={save} />}>
        <Field label="Section Title">
          <TextField value={marquee.title} onChange={(v) => setMarquee({ ...marquee, title: v })} />
        </Field>
        <div className="mt-4 space-y-2">
          {marquee.items.map((it, i) => (
            <div key={i} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 flex items-center gap-3">
              <div className="w-20 h-12 rounded-md bg-zinc-950 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                {it.image ? <img src={it.image} alt={it.label} className="max-w-full max-h-full object-contain" /> : <span className="text-zinc-500 text-[10px] tracking-wider font-bold">{it.label || 'NO LOGO'}</span>}
              </div>
              <div className="flex-1">
                <Field label="Brand Label (used if no image)"><TextField value={it.label} onChange={(v) => upd(i, 'label', v)} /></Field>
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-300 mb-1.5 block">Logo Image</label>
                <UploadButton onFile={(f) => onUpload(i, f)} />
                {it.image && (
                  <button onClick={() => upd(i, 'image', '')} className="mt-1 text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                    <Trash2 size={11} /> Clear
                  </button>
                )}
              </div>
              <button onClick={() => rem(i)} className="h-9 px-2 rounded-md bg-red-600/15 text-red-300 border border-red-900/40 flex items-center gap-1"><Trash2 size={14} /></button>
            </div>
          ))}
          <button onClick={add} className="w-full h-10 rounded-md border border-dashed border-zinc-700 text-zinc-300 text-sm flex items-center justify-center gap-1.5"><Plus size={14} /> Add Logo</button>
        </div>
      </Card>

      <Card title="Floating Social Visibility" desc="Show or hide each floating social icon. Links are configured in Promotions section." footer={<SaveButton onClick={save} />}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {['whatsapp', 'telegram', 'instagram', 'youtube'].map((k) => (
            <div key={k} className="flex items-center justify-between p-3 rounded-lg border border-zinc-800 bg-zinc-900/40">
              <div>
                <div className="text-zinc-100 text-sm font-bold capitalize">{k}</div>
                <div className="text-zinc-500 text-xs">{data.social?.[k] || '<no link configured>'}</div>
              </div>
              <Switch checked={visibility[k] !== false} onCheckedChange={(v) => setVisibility({ ...visibility, [k]: v })} />
            </div>
          ))}
        </div>
      </Card>
    </>
  );
}

function UploadButton({ onFile }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} className="hidden" />
      <button onClick={() => ref.current?.click()} className="h-8 px-2.5 rounded-md bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-200 text-xs flex items-center gap-1.5">
        <Upload size={12} /> Upload
      </button>
    </>
  );
}
